import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { SectionCard } from 'src/components/shared/SectionCard'
import { containsHtmlMarkup, sanitizeRichTextHtml } from 'src/utils/richTextHtml'

interface Props {
  instructions: string
}

const QUILL_INDENT_LEVELS = [1, 2, 3, 4, 5, 6, 7, 8]

/** Quill nests list items with `ql-indent-N` classes instead of nested lists. */
const quillIndentStyles = Object.fromEntries(
  QUILL_INDENT_LEVELS.map((level) => {
    return [`& .ql-indent-${level}`, { marginLeft: `${level * 1.5}em` }]
  }),
)

const richTextStyles = {
  ...tw`break-words text-shades-900`,
  '& h1, & h2, & h3, & h4, & h5, & h6': tw`mt-4 mb-2 font-semibold text-shades-900`,
  '& h1': tw`text-xl`,
  '& h2': tw`text-lg`,
  '& h3': tw`text-base`,
  '& h4, & h5, & h6': tw`text-sm`,
  '& p': tw`my-2`,
  '& ul, & ol': tw`my-2 pl-6`,
  '& ul': tw`list-disc`,
  '& ol': tw`list-decimal`,
  '& li': tw`my-1`,
  '& li[data-list="bullet"]': tw`list-disc`,
  ...quillIndentStyles,
  '& a': tw`text-primary-500 underline`,
  // Partners paste Slack emoji as images; keep them glyph-sized and inline with the text.
  '& img': tw`inline-block h-[1.2em] w-auto align-text-bottom`,
  '& > :first-child': tw`mt-0`,
  '& > :last-child': tw`mb-0`,
}

/** The partner's own review instructions: Quill rich text, sanitised; plain text verbatim. */
const PartnerInstructionsCardComponent = ({ instructions }: Props) => {
  const sanitizedHtml = useMemo(() => {
    return containsHtmlMarkup(instructions) ? sanitizeRichTextHtml(instructions) : null
  }, [instructions])

  return (
    <SectionCard title="Partner review instructions" dataTestId="document-review-instructions-card">
      {sanitizedHtml === null ? (
        <Typography variant="body2" css={tw`whitespace-pre-wrap text-shades-900`}>
          {instructions}
        </Typography>
      ) : (
        <Typography
          variant="body2"
          component="div"
          css={richTextStyles}
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
      )}
    </SectionCard>
  )
}

export const PartnerInstructionsCard = memo(PartnerInstructionsCardComponent)
